// Wall clock in her zone -> UTC instant.
//
// Two cases make this more than a subtraction, and both are real for Seattle:
//
//   Clocks forward   the wall clock she typed never occurs. 2027-03-14 02:30
//                    in America/Los_Angeles does not exist. Refused, not moved:
//                    temporal-polyfill's default 'compatible' disambiguation
//                    would silently shift it to 03:30 and schedule a date night
//                    at a time nobody picked.
//
//   Clocks back      the wall clock occurs twice. 2026-11-01 01:30 in
//                    America/Los_Angeles is both 08:30Z and 09:30Z. Resolved to
//                    the earlier, and flagged so the summary can disclose it.
//
// Asia/Seoul has never observed daylight saving, so neither case arises there.
//
// How the two are told apart: ask Temporal for both the 'earlier' and 'later'
// readings, then render each back to a wall clock. For an ambiguous time both
// readings round-trip to what she typed. For a nonexistent time neither does —
// they land either side of the gap. Comparing the instants alone cannot
// distinguish the cases, because in both the 'earlier' reading precedes the
// 'later' one.

import { Temporal } from 'temporal-polyfill';

/**
 * @typedef {object} WallClock
 * @property {number} year
 * @property {number} month  1-12
 * @property {number} day    1-31
 * @property {number} hour   0-23
 * @property {number} minute 0-59
 */

/**
 * @typedef {{ok: true, instant: string, epochSeconds: number, ambiguous: boolean, alternativeInstant: string | null}} WallClockResolved
 * @typedef {{ok: false, reason: 'nonexistent', zone: string, requested: WallClock, nearest: {before: string, after: string}}} WallClockNonexistent
 * @typedef {{ok: false, reason: 'invalid', detail: string}} WallClockInvalid
 * @typedef {WallClockResolved | WallClockNonexistent | WallClockInvalid} WallClockResult
 */

const FIELDS = ['year', 'month', 'day', 'hour', 'minute'];

function roundTripsTo(zoned, wall) {
  return FIELDS.every((field) => zoned[field] === wall[field]);
}

/**
 * Resolve a wall clock in a named zone to a single UTC instant.
 *
 * @param {WallClock} wall
 * @param {string} zone IANA zone identifier
 * @returns {WallClockResult}
 */
export function resolveWallClock(wall, zone) {
  if (typeof zone !== 'string' || zone.length === 0) {
    return { ok: false, reason: 'invalid', detail: 'a zone identifier is required' };
  }
  for (const field of FIELDS) {
    if (!Number.isInteger(wall?.[field])) {
      return { ok: false, reason: 'invalid', detail: `${field} must be an integer` };
    }
  }

  const base = { timeZone: zone, ...pickFields(wall) };

  let earlier;
  let later;
  try {
    earlier = Temporal.ZonedDateTime.from(base, { disambiguation: 'earlier' });
    later = Temporal.ZonedDateTime.from(base, { disambiguation: 'later' });
  } catch (error) {
    return { ok: false, reason: 'invalid', detail: error.message };
  }

  const earlierRoundTrips = roundTripsTo(earlier, wall);
  const laterRoundTrips = roundTripsTo(later, wall);

  // Neither reading gives back the wall clock she typed: it does not occur.
  if (!earlierRoundTrips && !laterRoundTrips) {
    return {
      ok: false,
      reason: 'nonexistent',
      zone,
      requested: pickFields(wall),
      nearest: {
        before: earlier.toPlainDateTime().toString(),
        after: later.toPlainDateTime().toString(),
      },
    };
  }

  const earlierInstant = earlier.toInstant();
  const laterInstant = later.toInstant();
  const ambiguous =
    earlierRoundTrips &&
    laterRoundTrips &&
    Temporal.Instant.compare(earlierInstant, laterInstant) !== 0;

  // Ambiguous times resolve to the earlier of the two occurrences.
  const chosen = earlierRoundTrips ? earlierInstant : laterInstant;

  return {
    ok: true,
    instant: chosen.toString(),
    epochSeconds: Math.floor(chosen.epochMilliseconds / 1000),
    ambiguous,
    alternativeInstant: ambiguous ? laterInstant.toString() : null,
  };
}

function pickFields(wall) {
  return {
    year: wall.year,
    month: wall.month,
    day: wall.day,
    hour: wall.hour,
    minute: wall.minute,
  };
}
